import React, { useEffect, useRef, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import { getFramework, useIdentity } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { getLastMousePoint, useProxyMouseEvents } from '@/utils/event-kit';
import { DirectionType, ILayerProps, IMainLayerProps } from '@/version-types';
import { type CropStagePlugin } from '../controller';
import { useMoveOperate } from '../hooks/use-move-operate';
import { useRotateOperate } from '../hooks/use-rotate-operate';
import { useScaleOperate } from '../hooks/use-scale-operate';
import { useSizeChangeOperate } from '../hooks/use-size-change';
import { getStageCropStore, useStageCropStore } from '../models/crop-stage-state';
import { CROP_STAGE_EVENT } from '../types';
import { computeAutoScale, getTranslateToStageCenter } from '../utils';
import CropGrid from './operate-stage/grid';
import Mask from './operate-stage/mask';
import MoveAction from './operate-stage/move-action';
import RotateAction from './operate-stage/rotate-action';
import ScaleAction from './operate-stage/scale-action';
import SizeAction from './operate-stage/size-action';
import './render-crop-stage.less';
import CropBg from './operate-stage/bg';

const getOperateBaseInfo = (stageWidth: number, stageHeight: number, scale: number) => {
    const element = document.getElementById('operate-stage-wrap');
    let translatePoint = {
        x: 0,
        y: 0,
    };
    if (element) {
        translatePoint = getTranslateToStageCenter(stageWidth * scale, stageHeight * scale);
    }
    return {
        width: stageWidth * scale,
        height: stageHeight * scale,
        translateX: -translatePoint.x,
        translateY: -translatePoint.y,
    };
};

const RenderCropStage: React.FC = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const originIndex = framework.service.frame.getActiveFrameId();
    const { $t } = getLocaleStore();

    const {
        currentCropStageScale,
        operateBaseInfo,
        isCropStageOperating,
        setOperateBaseInfo,
        setCurrentCropStageScale,
    } = useStageCropStore()((state) => state);

    const [mainLayer, setMainLayer] = useState<ILayerProps>(framework.service.frame.getMainLayer(originIndex)!);
    const cropController = framework.service.registration.getInstance<CropStagePlugin>('CropStagePlugin');
    const originMainLayer = useRef<ILayerProps | null>(null);
    const originLayers = useRef<ILayerProps[] | null>(null);
    const wheelY = useRef<number>(0);
    const scaleDistanceRef = useRef<number>(0);
    const zoomEndTimer = useRef<NodeJS.Timeout | null>(null);
    const { mouseEvents } = useProxyMouseEvents();
    const [isResize, setIsResize] = useState(0);

    useMoveOperate(currentCropStageScale);
    useScaleOperate(currentCropStageScale);
    useSizeChangeOperate(currentCropStageScale);
    useRotateOperate();

    useEffect(() => {
        const handler = framework.service.flatLayer.onChange(() => {
            setMainLayer(framework.service.frame.getMainLayer(originIndex)!);
        }, mainLayer.id);

        return () => {
            framework.service.events.off(handler);
        };
    }, [mainLayer.id]);

    const { width: stageWidth, height: stageHeight } = mainLayer;
    const maskURL = (mainLayer?.data as IMainLayerProps).maskURL || '';

    const computeScale = () => {
        const { width: currentWidth, height: currentHeight } = framework.service.edgelessCanvas.getSize();
        const frameId = framework.service.frame.getActiveFrameId();
        const mainLayer = framework.service.frame.getMainLayer(frameId)!;
        const { width: stageWidth, height: stageHeight } = mainLayer;
        const scale = computeAutoScale(
            currentWidth - 160,
            currentHeight - 160,
            stageWidth,
            stageHeight,
            framework.service.edgelessCanvas.fitPadding[0],
        );
        return scale;
    };

    useEffect(() => {
        setCurrentCropStageScale(computeScale());
        const handler = framework.service.edgelessCanvas.onSizeChange(() => {
            cropController.syncCropFrameViewport(framework.service.frame.getActiveFrameId());
            setIsResize((prev) => prev + 1);
        });

        return () => {
            framework.service.events.off(handler);
        };
    }, []);

    useEffect(() => {
        if (isCropStageOperating) return;
        setOperateBaseInfo(getOperateBaseInfo(stageWidth, stageHeight, currentCropStageScale));
    }, [currentCropStageScale, isResize, isCropStageOperating, stageHeight, stageWidth]);

    useEffect(() => {
        const handler = framework.service.editor.onMouseDown(() => {
            framework.service.events.emit(CROP_STAGE_EVENT.CONFIRM);
            return true;
        }, 1000);

        return () => {
            framework.service.events.off(handler);
        };
    }, []);

    useEffect(() => {
        const handler = framework.service.editor.onWheel((ev) => {
            wheelY.current += ev.deltaY;

            if (Math.abs(wheelY.current) >= 2) {
                if (ev.deltaY > 0) {
                    wheelY.current = 0;
                    scaleDistanceRef.current = scaleDistanceRef.current - 1;
                } else if (ev.deltaY < 0) {
                    wheelY.current = 0;
                    scaleDistanceRef.current = scaleDistanceRef.current + 1;
                }

                if (!originMainLayer.current || !originLayers.current) {
                    const frameId = framework.service.frame.getActiveFrameId();
                    originMainLayer.current = framework.service.frame.getMainLayer(frameId);
                    originLayers.current = framework.service.frame.getLayers(frameId);
                }

                if (originMainLayer.current) {
                    cropController.scaleMainLayerOperate(
                        originMainLayer.current,
                        originLayers.current,
                        scaleDistanceRef.current > 0 ? DirectionType.POSITIVE : DirectionType.OPPOSITE,
                        getLastMousePoint(),
                        getStageCropStore(framework.identity).currentCropStageScale,
                        scaleDistanceRef.current,
                    );
                }
            }

            if (zoomEndTimer.current) clearTimeout(zoomEndTimer.current);

            zoomEndTimer.current = setTimeout(() => {
                scaleDistanceRef.current = 0;
                originMainLayer.current = null;
                originLayers.current = null;
            }, 300);

            return true;
        }, 1000);
        return () => {
            framework.service.events.off(handler);
        };
    }, []);

    useEffect(() => {
        const item = framework.service.customData.add(originIndex, 'crop-stage-bg', <CropBg />, 'bottom', false);
        return () => {
            framework.service.customData.remove(item);
        };
    }, []);

    return (
        <div className="l-operate-stage" id="crop-stage-wrap">
            <div
                className="l-operate-stage_wrap"
                style={{
                    width: operateBaseInfo.width,
                    height: operateBaseInfo.height,
                    transform: `translate(${operateBaseInfo.translateX}px, ${operateBaseInfo.translateY}px)`,
                }}
                id="operate-stage-wrap"
                {...mouseEvents}
            >
                {!isCropStageOperating && (
                    <div className="l-operate-stage_confirm">
                        <div
                            className="btn-cancel"
                            onClick={() => {
                                framework.service.events.emit(CROP_STAGE_EVENT.CANCEL);
                            }}
                        >
                            <IconFont name="CrossCircle" fontSize={16} color="var(--content-system-primary, #1C1D1F)" />
                            <div>{$t((v) => v.collapse.adjustCollapse.cancelText)}</div>
                        </div>
                        <div
                            className="btn-confirm"
                            onClick={() => {
                                framework.service.events.emit(CROP_STAGE_EVENT.CONFIRM);
                            }}
                        >
                            <IconFont name="CheckCircle" fontSize={16} color="var(--content-tag)" />
                            <div>{$t((v) => v.collapse.adjustCollapse.okText)}</div>
                        </div>
                    </div>
                )}
                <Mask
                    maskURL={maskURL}
                    stageWidth={stageWidth}
                    stageHeight={stageHeight}
                    isOperating={isCropStageOperating}
                    scale={currentCropStageScale}
                />
                <MoveAction width={stageWidth * currentCropStageScale} height={stageHeight * currentCropStageScale} />

                <SizeAction />

                <ScaleAction />

                <CropGrid />

                <RotateAction isOperating={isCropStageOperating} />
            </div>
        </div>
    );
});

export default RenderCropStage;
